<template>
    <BaseDialog
        :showDialog="showDialog"
        :headline="headlineText"
        :close="close"
        maxDialogWidth="900"
        maxCardHeight="800"
        :alvqa="alvqa"
    >
        <template v-slot:dialogText>
            <v-container fluid>

                <!-- Cascade hint -->
                <!-- <v-row class="mb-1">
                    <v-col cols="12" class="py-1 cascade-hint">
                        <v-icon small color="standardBrownIcon" class="mr-2">info</v-icon>
                        {{ $t('lang_ACCOUNT_COMPANY_CAPTION_DEFAULT_MAPPINGS_CASCADE_HINT') }}
                    </v-col>
                </v-row> -->

                <!-- Language tabs -->
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-tabs
                            v-model="activeLanguageIndex"
                            background-color="transparent"
                            slider-color="buttonStandard"
                            show-arrows
                            :alvqa="alvqa + '-language-tabs'"
                        >
                            <v-tab
                                v-for="lang in activeLanguages"
                                :key="lang"
                                :alvqa="alvqa + '-language-tab-' + lang"
                            >
                                {{ languageLabel(lang) }}
                            </v-tab>

                            <!-- Add-language affordance -->
                            <v-menu offset-y v-if="availableLanguagesToAdd.length">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        text
                                        small
                                        class="ma-1"
                                        v-bind="attrs"
                                        v-on="on"
                                        :alvqa="alvqa + '-button-add-language'"
                                    >
                                        <v-icon small class="mr-1">add</v-icon>
                                        {{ $t('lang_ACCOUNT_COMPANY_BUTTON_DEFAULT_MAPPINGS_ADD_LANGUAGE') }}
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item
                                        v-for="lang in availableLanguagesToAdd"
                                        :key="lang"
                                        @click="addLanguage(lang)"
                                        :alvqa="alvqa + '-add-language-option-' + lang"
                                    >
                                        <v-list-item-title>{{ languageLabel(lang) }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-tabs>
                    </v-col>
                </v-row>

                <!-- Matrix for active language -->
                <v-row no-gutters class="mt-4" v-if="currentLanguage">
                    <v-col cols="12">
                        <v-simple-table dense class="default-mappings-matrix">
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="col-statement-type">
                                            {{ $t('lang_GENERIC_CAPTION_SUBTYPE') }}
                                        </th>
                                        <th class="col-mapping">
                                            {{ $t('lang_GENERIC_CAPTION_ABBREVIATION') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="row in statementTypeRows"
                                        :key="row.value"
                                    >
                                        <td class="col-statement-type">
                                            {{ row.fallbackText && !$te(row.text) ? row.fallbackText : $t(row.text) }}
                                        </td>
                                        <td class="col-mapping">
                                            <DefaultMappingCell
                                                :mappingType="row.value"
                                                :language="currentLanguage"
                                                :value="cellValue(row.value, currentLanguage)"
                                                :alvqa="alvqa"
                                                :key="row.value + '|' + currentLanguage"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-col>
                </v-row>

            </v-container>
        </template>

        <template v-slot:dialogActions>
            <v-container fluid>
                <v-row>
                    <v-spacer />
                    <v-col cols="auto" class="text-right">
                        <BaseButton
                            label="lang_GENERAL_BUTTON_CLOSE"
                            @click="close"
                            :alvqa="alvqa + '-button-close'"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import DefaultMappingCell from './DefaultMappingCell'
import {
    DropdownCompanyDefaultMappingStatementTypes,
    DropdownErpInvoicesLanguages,
    DropdownAzMetadataLanguages,
} from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: 'CompanyDefaultMappingsDialog',
    components: { BaseDialog, BaseButton, DefaultMappingCell },
    data() {
        return {
            alvqa: 'company-default-mappings-dialog',
            activeLanguageIndex: 0,
            extraLanguages: [], // user-added tabs that don't yet have rows on BE
        }
    },
    computed: {
        showDialog() {
            return this.$store.getters.getShowCompanyDefaultMappingsDialog
        },
        context() {
            return this.$store.getters.getCompanyDefaultMappingsContext || {}
        },
        defaults() {
            return this.$store.getters.getCompanyDefaultMappings || []
        },
        headlineText() {
            const name = this.context.companyName || ''
            const base = this.$t('lang_ACCOUNT_COMPANY_CAPTION_DEFAULT_MAPPINGS_HEADLINE')
            return name ? base + ' — ' + name : base
        },
        statementTypeRows() {
            const type = this.context.companyType
            if (!type) return []
            return DropdownCompanyDefaultMappingStatementTypes.filter(r => r.companyType === type)
        },
        // Languages always offered as tabs: user's UI language + English (deduped, in that order).
        baseLanguages() {
            const userLocale = this.$store.getters.getUserLanguage || this.$i18n.locale || 'en'
            return userLocale === 'en' ? ['en'] : [userLocale, 'en']
        },
        // Languages = base ∪ languages present in current defaults ∪ user-added tabs
        activeLanguages() {
            const set = new Set(this.baseLanguages)
            this.defaults.forEach(d => { if (d.language) set.add(d.language) })
            this.extraLanguages.forEach(l => set.add(l))
            return Array.from(set)
        },
        currentLanguage() {
            return this.activeLanguages[this.activeLanguageIndex] || null
        },
        allKnownLanguages() {
            // Union of the two existing language dropdowns we already ship
            const langs = new Set()
            DropdownErpInvoicesLanguages.forEach(l => langs.add(l.value))
            DropdownAzMetadataLanguages.forEach(l => langs.add(l.value))
            return Array.from(langs)
        },
        availableLanguagesToAdd() {
            return this.allKnownLanguages.filter(l => !this.activeLanguages.includes(l))
        },
    },
    methods: {
        cellValue(mappingType, language) {
            const row = this.defaults.find(d =>
                d.mapping_type === mappingType && d.language === language
            )
            return row ? row.mapping_id : null
        },
        languageLabel(value) {
            // Prefer the human-readable text from AzMetadata; fall back to upper-case code
            const fromMeta = DropdownAzMetadataLanguages.find(l => l.value === value)
            if (fromMeta) return fromMeta.text
            const fromErp = DropdownErpInvoicesLanguages.find(l => l.value === value)
            if (fromErp) return this.$t(fromErp.text)
            return (value || '').toUpperCase()
        },
        addLanguage(lang) {
            if (!this.extraLanguages.includes(lang)) this.extraLanguages.push(lang)
            this.$nextTick(() => {
                this.activeLanguageIndex = this.activeLanguages.indexOf(lang)
            })
        },
        close() {
            this.$store.commit('setShowCompanyDefaultMappingsDialog', false)
            this.$store.commit('setCompanyDefaultMappingsContext', null)
            this.$store.commit('setCompanyDefaultMappings', [])
            this.$store.commit('clearCompanyDefaultMappingsPools')
            this.extraLanguages = []
            this.activeLanguageIndex = 0
        },
    },
    watch: {
        showDialog(open) {
            if (open) {
                this.activeLanguageIndex = 0
                this.extraLanguages = []
            }
        },
    },
}
</script>

<style scoped>
.cascade-hint {
    font-size: 13px;
    color: var(--v-standardBrownIcon-base, #8a6d3b);
    display: flex;
    align-items: center;
}
.default-mappings-matrix th {
    font-weight: 600;
    text-align: left;
}
.default-mappings-matrix .col-statement-type {
    width: 38%;
    white-space: nowrap;
}
.default-mappings-matrix .col-mapping {
    width: 62%;
}
.default-mappings-matrix td {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    vertical-align: middle;
}
</style>
